import { useLocalStorage } from "@app/hooks/useLocalStorage";
import { useEffect, useMemo, useState } from "react";

// this will not save to local storage
export const useChartDataSelectorState = ({
  data1DefaultId,
  data2DefaultId,
  data3DefaultId,
}: {
  data1DefaultId?: string;
  data2DefaultId?: string;
  data3DefaultId?: string;
}) => {
  const [data1Id, onData1IdChange] = useState(data1DefaultId);
  const [data2Id, onData2IdChange] = useState(data2DefaultId);
  const [data3Id, onData3IdChange] = useState(data3DefaultId);

  useEffect(() => {
    if (data1DefaultId) {
      onData1IdChange(data1DefaultId);
    }
  }, [data1DefaultId]);

  useEffect(() => {
    if (data2DefaultId) {
      onData2IdChange(data2DefaultId);
    }
  }, [data2DefaultId]);

  useEffect(() => {
    if (data3DefaultId) {
      onData3IdChange(data3DefaultId);
    }
  }, [data3DefaultId]);

  return {
    data1Id,
    onData1IdChange,
    data2Id,
    onData2IdChange,
    data3Id,
    onData3IdChange,
  };
};

export const useChartDataSelector = ({
  storageKey,
  data1DefaultId,
  data2DefaultId,
  data3DefaultId,
}: {
  storageKey?: string;
  data1DefaultId?: string;
  data2DefaultId?: string;
  data3DefaultId?: string;
}) => {
  const [data1Id, onData1IdChange] = useLocalStorage<string | undefined>(
    storageKey ? `${storageKey}_data1Id` : undefined,
    data1DefaultId,
  );
  const [data2Id, onData2IdChange] = useLocalStorage<string | undefined>(
    storageKey ? `${storageKey}_data2Id` : undefined,
    data2DefaultId,
  );
  const [data3Id, onData3IdChange] = useLocalStorage<string | undefined>(
    storageKey ? `${storageKey}_data3Id` : undefined,
    data3DefaultId,
  );

  useEffect(() => {
    if (data1DefaultId && !data1Id) {
      onData1IdChange(data1DefaultId);
    }
  }, [data1DefaultId, data1Id, onData1IdChange]);

  useEffect(() => {
    if (data2DefaultId && !data2Id) {
      onData2IdChange(data2DefaultId);
    }
  }, [data2DefaultId, data2Id, onData2IdChange]);

  useEffect(() => {
    if (data3DefaultId && !data3Id) {
      onData3IdChange(data3DefaultId);
    }
  }, [data3DefaultId, data3Id, onData3IdChange]);

  return useMemo(
    () => ({
      data1Id,
      onData1IdChange,
      data2Id,
      onData2IdChange,
      data3Id,
      onData3IdChange,
    }),
    [
      data1Id,
      onData1IdChange,
      data2Id,
      onData2IdChange,
      data3Id,
      onData3IdChange,
    ],
  );
};

export type UseChartDataSelector = ReturnType<typeof useChartDataSelector>;
